import { useEffect, useRef } from 'react';

/**
 * Thin React wrapper around Google Maps JS API AdvancedMarkerElement.
 *
 * Props:
 *   map       – google.maps.Map instance (required once ready)
 *   position  – { lat, lng } literal
 *   imgUrl    – URL or data-URL of the marker image
 *   size      – diameter in CSS pixels (number)
 *   zIndex    – optional z-index
 *   onClick   – optional click handler
 *   extraClass – optional extra class(es) applied to the wrapper div
 *   label     – optional text label shown to the right of the marker
 */
export default function AdvancedImageMarker({
  map,
  position,
  imgUrl,
  size = 44,
  zIndex = 0,
  onClick,
  extraClass = '',
  label = '',
  offset = null,
  offsetScale = 1,
  markerScale = null,
  showAnchorConnector = false,
  connectorColor = '#FFD700',
}) {
  const markerRef = useRef(null);
  const wrapperRef = useRef(null);
  const imgRef = useRef(null);
  const labelRef = useRef(null);
  const connectorRef = useRef(null);
  const connectorLineRef = useRef(null);
  const connectorDotRef = useRef(null);
  const clickRef = useRef(onClick);
  const markerLibRequestedRef = useRef(false);

  // Keep ref updated with latest handler
  useEffect(() => {
    clickRef.current = onClick;
  }, [onClick]);

  // Create marker when map becomes available
  useEffect(() => {
    let cancelled = false;

    const mountMarker = async () => {
      if (!map || !position || !window.google) return;

      // Ensure marker library exists
      if (!window.google.maps.marker?.AdvancedMarkerElement) {
        if (typeof window.google.maps.importLibrary === 'function' && !markerLibRequestedRef.current) {
          markerLibRequestedRef.current = true;
          try {
            await window.google.maps.importLibrary('marker');
          } catch (err) {
            console.error('[AdvancedImageMarker] Failed to import marker library', err);
            return;
          } finally {
            markerLibRequestedRef.current = false;
          }
        } else {
          return;
        }
      }

      if (cancelled || !window.google.maps.marker?.AdvancedMarkerElement) return;

      const { AdvancedMarkerElement } = window.google.maps.marker;

      const img = document.createElement('img');
      imgRef.current = img;
      img.draggable = false;

      const wrapper = document.createElement('div');
      wrapperRef.current = wrapper;
      wrapper.className = `scalable-marker ${extraClass}`.trim();
      wrapper.style.position = 'relative';
      wrapper.style.display = 'block';
      wrapper.style.userSelect = 'none';
      wrapper.style.webkitUserSelect = 'none';
      wrapper.style.webkitUserDrag = 'none';
      wrapper.style.setProperty('--marker-offset-x', '0px');
      wrapper.style.setProperty('--marker-offset-y', '0px');
      wrapper.appendChild(img);

      const marker = new AdvancedMarkerElement({
        map,
        position,
        content: wrapper,
        zIndex,
      });

      marker.addListener('gmp-click', () => {
        if (clickRef.current) clickRef.current();
      });

      markerRef.current = marker;
    };

    mountMarker();

    return () => {
      cancelled = true;
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      wrapperRef.current = null;
      imgRef.current = null;
      labelRef.current = null;
      connectorRef.current = null;
      connectorLineRef.current = null;
      connectorDotRef.current = null;
    };
  }, [map, position?.lat, position?.lng, extraClass, zIndex]);

  // Update image source and size WITHOUT recreating marker
  useEffect(() => {
    if (imgRef.current) {
      imgRef.current.src = imgUrl;
      const adjustedSize = size * 0.9;
      imgRef.current.style.width = `${adjustedSize}px`;
      imgRef.current.style.height = 'auto';
      imgRef.current.style.objectFit = 'cover';
      imgRef.current.style.position = 'relative';
      imgRef.current.style.zIndex = '2';
      imgRef.current.style.userSelect = 'none';
      imgRef.current.style.webkitUserSelect = 'none';
      imgRef.current.style.webkitUserDrag = 'none';

      if (wrapperRef.current) {
        wrapperRef.current.style.width = `${adjustedSize}px`;
        wrapperRef.current.style.height = `${adjustedSize}px`;
      }
    }
  }, [imgUrl, size]);

  // Allow specific marker groups to opt into a gentler scale than the global map marker scale.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const nextScale = Number(markerScale);
    if (Number.isFinite(nextScale) && nextScale > 0) {
      wrapper.style.setProperty('--marker-scale', String(nextScale));
    } else {
      wrapper.style.removeProperty('--marker-scale');
    }
  }, [markerScale]);

  // Offset only the visual content; the AdvancedMarkerElement position remains exact.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const offsetX = Number(offset?.x) || 0;
    const offsetY = Number(offset?.y) || 0;
    const scale = Number(offsetScale) || 1;
    const adjustedSize = size * 0.9;
    const shouldDrawConnector = showAnchorConnector && Math.hypot(offsetX, offsetY) > 4;

    wrapper.style.setProperty('--marker-offset-x', `${offsetX}px`);
    wrapper.style.setProperty('--marker-offset-y', `${offsetY}px`);

    if (!shouldDrawConnector) {
      if (connectorRef.current) {
        connectorRef.current.remove();
        connectorRef.current = null;
        connectorLineRef.current = null;
        connectorDotRef.current = null;
      }
      return;
    }

    if (!connectorRef.current) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');

      svg.style.position = 'absolute';
      svg.style.left = '0';
      svg.style.top = '0';
      svg.style.overflow = 'visible';
      svg.style.pointerEvents = 'none';
      svg.style.zIndex = '1';

      line.setAttribute('stroke-width', '2');
      line.setAttribute('stroke-linecap', 'round');
      line.setAttribute('opacity', '0.85');

      dot.setAttribute('r', '4');
      dot.setAttribute('fill', '#FFFFFF');
      dot.setAttribute('stroke-width', '2');

      svg.appendChild(line);
      svg.appendChild(dot);
      wrapper.insertBefore(svg, imgRef.current || wrapper.firstChild);

      connectorRef.current = svg;
      connectorLineRef.current = line;
      connectorDotRef.current = dot;
    }

    const anchorX = adjustedSize / 2;
    const anchorY = adjustedSize;
    const trueAnchorX = anchorX - (offsetX / scale);
    const trueAnchorY = anchorY - (offsetY / scale);

    connectorRef.current.setAttribute('width', `${adjustedSize}`);
    connectorRef.current.setAttribute('height', `${adjustedSize}`);
    connectorRef.current.setAttribute('viewBox', `0 0 ${adjustedSize} ${adjustedSize}`);

    connectorLineRef.current.setAttribute('x1', `${anchorX}`);
    connectorLineRef.current.setAttribute('y1', `${anchorY}`);
    connectorLineRef.current.setAttribute('x2', `${trueAnchorX}`);
    connectorLineRef.current.setAttribute('y2', `${trueAnchorY}`);
    connectorLineRef.current.setAttribute('stroke', connectorColor);

    connectorDotRef.current.setAttribute('cx', `${trueAnchorX}`);
    connectorDotRef.current.setAttribute('cy', `${trueAnchorY}`);
    connectorDotRef.current.setAttribute('stroke', connectorColor);
  }, [offset?.x, offset?.y, offsetScale, size, showAnchorConnector, connectorColor]);

  // Update position WITHOUT recreating marker
  useEffect(() => {
    if (markerRef.current && position) {
      markerRef.current.position = position;
    }
  }, [position?.lat, position?.lng]);

  // Update zIndex WITHOUT recreating marker
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.zIndex = zIndex;
    }
  }, [zIndex]);

  // Update CSS class WITHOUT recreating marker
  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.className = `scalable-marker ${extraClass}`.trim();
    }
  }, [extraClass]);

  // Update label WITHOUT recreating marker
  useEffect(() => {
    if (!wrapperRef.current) return;

    // Remove existing label if it exists
    if (labelRef.current) {
      labelRef.current.remove();
      labelRef.current = null;
    }

    // Add new label if provided
    if (label) {
      const labelEl = document.createElement('span');
      labelEl.textContent = label;
      labelEl.style.position = 'absolute';
      labelEl.style.left = '100%';
      labelEl.style.top = '50%';
      labelEl.style.transform = 'translateY(-50%)';
      labelEl.style.marginLeft = '6px';
      labelEl.style.color = '#FFFFFF';
      labelEl.style.fontWeight = '700';
      labelEl.style.fontSize = '16px';
      labelEl.style.letterSpacing = '0.2px';
      labelEl.style.whiteSpace = 'nowrap';
      labelEl.style.textShadow = '0 0 3px rgba(0,0,0,0.8), 0 0 6px rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.9)';
      labelEl.style.userSelect = 'none';
      labelEl.style.webkitUserSelect = 'none';
      labelEl.style.webkitUserDrag = 'none';
      labelEl.className = 'marker-label';
      wrapperRef.current.appendChild(labelEl);
      labelRef.current = labelEl;
    }
  }, [label]);

  return null; // No React DOM output – marker lives in Maps layer.
}
